/**
 * archetypes.js (en) — all English copy for the eight personas.
 *
 * The structure (id, cluster, bricksProfile) lives in `src/data.js`.
 * Never add logic or numbers the app calculates with here.
 *
 * Note: the arrays (kw, friction, consequence, leadership, needs, mismatch,
 * energycost, wd, ls, ct) must have the same length in every language —
 * the UI relies on a matching number of items.
 *
 * Persona names: Maker, Grower, Achiever, Thinker, Connector, Team Player,
 * Stabiliser, Innovator. Keep these consistent inside body copy too.
 */
const archetypes = {
  maker: {
    name: 'Maker',
    kw: ['creating', 'freedom', 'experiment'],
    short: 'You make things. Ideas only really gain value for you once you can turn them into something visible or tangible.',
    motivation: 'The freedom to create, to experiment and to put something down quickly.',
    energy_from: 'Room to think up something new, try it out and turn it straight into action.',
    frustration: 'Too many rules, slow decision-making and environments where initiative is held back.',
    strengths: 'Brings creativity, energy and momentum. Turns ideas into something concrete.',
    bricks: 'Flexible workstations, creative zones, project tables, whiteboards and places where you can make, sketch or test right away.',
    bricksProfileText: {
      focus: 'Focus spaces work for the Maker in short bursts — to develop a concept or process feedback. Not prolonged isolation, but targeted moments of refinement.',
      work: 'Standard workstations are fine for the Maker as long as they are flexible and sit close to creative zones. A fixed desk with no making space around it feels restrictive.',
      meeting: 'Meeting spaces are only valuable to the Maker when they are small and fast. A ten-minute stand-up beats an hour in a boardroom.',
      project: 'Creative spaces are the Maker\'s core. Whiteboards, project tables, prototyping materials and room to make ideas visible — this is the natural habitat. The more space to make, the better.',
      team: 'Collaboration spaces are crucial for the Maker when they are active — large tables, writable walls, room to move. Not formal meeting environments but zones where something gets built.',
      learning: 'Learning spaces interest the Maker when they are immediately applicable. Learning by doing suits better than formal learning environments. Informal knowledge zones work best.',
      retreat: 'Retreat spaces are a release valve for the Maker, not a workspace. A short break to reset creativity is useful but not a fixed part of the rhythm.',
      social: 'Informal spaces are a welcome source of inspiration for the Maker. A chance encounter at the coffee point can unstick an idea that had stalled.',
    },
    bytes: 'Light, fast tools that support creation rather than slow it down. Think Miro, Figma, note-taking tools and simple collaboration platforms.',
    behavior: 'Give direction on the big picture, but leave room in the approach. This persona thrives on trust, autonomy and experiment.',
    friction: [
      'Rigid procedures push your energy down.',
      'Too much control feels like mistrust.',
      'When everything has to be discussed first, your initiative drains away.',
    ],
    consequence: [
      'Without room to make things, your energy turns into frustration.',
      'You become less visible when your creative force goes unused.',
      'The organisation loses renewal without anyone noticing straight away.',
    ],
    leadership: [
      'Give freedom within clear boundaries.',
      'Steer on results, not on every single step.',
      'Make experimenting normal and safe.',
    ],
    // `needs` = the same leadership points, but in the first person.
    // `leadership` is written to a manager, about you — an odd mirror to find
    // inside your own profile. These lines are yours to read out in the
    // conversation; they live on the conversation screen
    // (src/native/ConversationPrep.jsx). `leadership` stays for the Teams and
    // Company modules.
    needs: [
      'I need freedom in how I approach something — give me the frame, not the steps.',
      'Judge me on what I deliver, not on how I got there.',
      'I want to try things out without everything having to be cleared first.',
    ],
    lquote: 'Manage this person too tightly and you will not lose the chaos — you will lose the creativity.',
    // `quote` = the same truth as `lquote`, but said to you. `lquote` talks
    // about you in the third person — as the closing line of your own profile
    // that read like a case file. Only the app profile screen uses `quote`;
    // the web, the PDF and the persona library keep `lquote`.
    quote: 'Manage you too tightly and the chaos does not go — your creativity does.',
    mismatch: [
      'Workplaces without making space or visual tools.',
      'Cultures where conformity matters more than initiative.',
      'Meetings with no room to try something out.',
    ],
    energycost: [
      'Too many rules and too much control.',
      'No room to test ideas.',
      'Always having to wait for permission.',
    ],
    wd: [
      'Creative zones with whiteboards and project tables',
      'Flexible workstations that move with the work',
      'Room to make ideas visible',
    ],
    ls: [
      'Give direction, not a fixed script',
      'Reward initiative visibly',
      'Make learning by doing normal',
    ],
    ct: [
      'Combines strongly with the Thinker',
      'Often clashes with the Stabiliser',
      'Brings ideas and energy to the team',
    ],
  },
  groeier: {
    name: 'Grower',
    kw: ['learning', 'development', 'forward'],
    short: 'You grow. Standing still does not feel safe to you — it feels like losing energy.',
    motivation: 'Learning new things, developing yourself and always getting a step further.',
    energy_from: 'Challenge, feedback, new experiences and visible development.',
    frustration: 'Repetition, routine and environments with little room to learn or grow.',
    strengths: 'Learns quickly, adapts easily and encourages others to develop too.',
    bricks: 'Learning spaces, paired workstations, informal knowledge zones and places where coaching and feedback happen naturally.',
    bricksProfileText: {
      focus: 'Focus spaces are useful for the Grower for reflection and depth after new experiences. Not prolonged isolation but targeted moments to process.',
      work: 'Standard workstations are fine for the Grower as long as the environment offers enough challenge and variety. A fixed spot beside stimulating colleagues works better than a quiet corner.',
      meeting: 'Meeting spaces are valuable to the Grower when they allow sparring, receiving feedback and testing ideas. Meeting for the sake of meeting has the opposite effect.',
      project: 'Creative spaces appeal to the Grower because they make room for experiment and new approaches. Not as an end in themselves but as a way to learn and find inspiration.',
      team: 'Collaboration spaces are valuable to the Grower as a learning environment. Working with people who think differently or know more is energising. Co-creation and knowledge sharing in action are the ideal collaboration setting.',
      learning: 'Learning spaces are the Grower\'s core. Knowledge hubs, coaching rooms, places for feedback and exchange — this is where the Grower does best. Visible access to inspiration and new knowledge is essential.',
      retreat: 'Retreat spaces are useful to the Grower as a moment to process intensive learning experiences. A brief return to oneself before the next challenge begins.',
      social: 'Informal spaces are a source of unplanned knowledge exchange for the Grower. Spontaneous conversations about work and inspiration bring energy and new ideas.',
    },
    bytes: 'Tools that make development visible, make feedback easy and keep new knowledge within reach.',
    behavior: 'Offer perspective, challenge and opportunities to learn. Let development be part of the work itself.',
    friction: [
      'Without challenge your motivation dries up quickly.',
      'Little feedback feels like standing still to you.',
      'When development is promised but never delivered, your trust fades.',
    ],
    consequence: [
      'You keep working, but you stop genuinely growing.',
      'The chance grows that you will look for development elsewhere.',
      'Your energy drops once you no longer sense a future.',
    ],
    leadership: [
      'Discuss learning goals actively.',
      'Give challenging assignments alongside routine.',
      'Make growth visible and concrete.',
    ],
    needs: [
      'I want to talk about my development, not only about my tasks.',
      'Alongside the steady work, give me something I am not good at yet.',
      'I need feedback to know whether I am actually moving forward.',
    ],
    lquote: 'Fail to challenge this person and you lose the energy first — and the person soon after.',
    quote: 'If nobody challenges you, you lose your energy first — and your reason to stay soon after.',
    mismatch: [
      'Roles or teams where everything stays the same.',
      'A culture without feedback.',
      'Environments where development never gets past the talking stage.',
    ],
    energycost: [
      'Routine without challenge.',
      'Getting no feedback.',
      'No room to grow.',
    ],
    wd: [
      'Knowledge hubs and informal learning zones',
      'Places for coaching, feedback and exchange',
      'Visible access to inspiration and new knowledge',
    ],
    ls: [
      'Make learning part of the work',
      'Give growth language and direction',
      'Provide regular feedback',
    ],
    ct: [
      'Reinforces the Achiever well',
      'Can unsettle Stabilisers',
      'Brings development and momentum to teams',
    ],
  },
  presteerder: {
    name: 'Achiever',
    kw: ['results', 'pace', 'grip'],
    short: 'You go for results. Progress, sharpness and genuinely finishing something are what energise you.',
    motivation: 'Hitting targets, seeing progress and keeping a grip on what matters.',
    energy_from: 'Clear goals, visible progress, focus and tangible results.',
    frustration: 'Ambiguity, delay, endless discussion and teams without an edge.',
    strengths: 'Brings focus, pace and results. Helps teams avoid getting stuck.',
    bricks: 'Focus spaces, screened work zones, project rooms without distraction and places where progress is visible.',
    bricksProfileText: {
      focus: 'Focus spaces are the absolute core for the Achiever. Deep focus zones without noise, interruption or visual distraction are not a luxury but a necessity. This is where most of the work gets done and most of the energy is regained.',
      work: 'Standard workstations are fine for the Achiever provided they are quiet, ergonomic and well equipped. Certainty about availability on peak days is essential — no hot-desking.',
      meeting: 'Meeting spaces are wasted time for the Achiever unless they lead to a clear decision. Small, functional and quickly available — no large boardrooms for routine discussion.',
      project: 'Creative spaces hold little appeal for the Achiever. Experiment without direction feels like wasted time. Only interesting when there is a concrete goal and a clear outcome attached.',
      team: 'Collaboration spaces are only valuable to the Achiever when they are results-driven. Focused project environments with visible progress and a clear goal are a good fit.',
      learning: 'Learning spaces are relevant to the Achiever when they contribute directly to better performance. Not learning for learning\'s sake — learning to become sharper, faster or more effective.',
      retreat: 'Retreat spaces are functional for the Achiever as a moment of recovery. A short recharge in order to carry on fully focused — nothing more than that.',
      social: 'Informal spaces cost the Achiever more energy than they give, unless the conversation has substance and purpose. Casual catching up is not a priority.',
    },
    bytes: 'Dashboards, task tools, planning tools and systems that give immediate insight into goals, deadlines and progress.',
    behavior: 'Be clear about goals, expectations and priorities. Give room, but keep the direction sharp.',
    friction: [
      'Ambiguity slows your pace immediately.',
      'Meeting without a decision feels like wasted energy.',
      'Constantly shifting priorities cost you trust.',
    ],
    consequence: [
      'You check out mentally when results take too long to arrive.',
      'You still deliver, but no longer at your best.',
      'The drive fades once the sharpness disappears.',
    ],
    leadership: [
      'Be direct and clear.',
      'Acknowledge results explicitly.',
      'Keep priorities visible and stable.',
    ],
    needs: [
      'Tell me straight — I would rather have a clear no than a vague maybe.',
      'Say it when something worked; otherwise I do not know whether it counted.',
      'I need priorities that hold, or I will run hard in the wrong direction.',
    ],
    lquote: 'Without a clear goal this engine keeps running — but goes nowhere.',
    quote: 'Without a clear goal you keep running at full power — but you get nowhere.',
    mismatch: [
      'Workplaces without focus.',
      'Cultures where process matters more than results.',
      'Prolonged vagueness about direction.',
    ],
    energycost: [
      'Unclear goals.',
      'Meeting without a decision.',
      'Nowhere to focus.',
    ],
    wd: [
      'Deep focus zones',
      'Visual progress boards',
      'Project spaces close to quiet workstations',
    ],
    ls: [
      'Make priorities concrete',
      'Acknowledge progress and output',
      'Steer on outcome, not on busyness',
    ],
    ct: [
      'Strong alongside the Innovator',
      'Can clash with the Connector',
      'Keeps teams sharp and moving',
    ],
  },
  denker: {
    name: 'Thinker',
    kw: ['analysis', 'depth', 'substance'],
    short: 'You think first, then act. You want to understand how something really works before you move.',
    motivation: 'Getting to the bottom of complexity and delivering quality based on insight.',
    energy_from: 'Calm, depth, analysis and the room to genuinely understand a question.',
    frustration: 'Superficiality, haste and decisions taken without a substantive basis.',
    strengths: 'Brings depth, precision and quality. Sees what others miss.',
    bricks: 'Quiet focus spaces, focus rooms, library-like zones and environments with as little noise as possible.',
    bricksProfileText: {
      focus: 'Focus spaces are the single most essential workplace for the Thinker of all the archetypes. Fully quiet, enclosed zones without visual or auditory stimuli are not a treat but a basic condition for functioning well.',
      work: 'Standard workstations only work for the Thinker when they are quiet enough. Open-plan floors with a lot of movement and noise are a cognitive burden. A fixed, quiet spot provides an anchor.',
      meeting: 'Meeting spaces are draining for the Thinker unless the discussion is properly prepared. Impromptu meetings or discussions without a sharp question cost a disproportionate amount of energy.',
      project: 'Creative spaces only suit the Thinker when they have calm and structure built in. Open, cluttered maker spaces have the opposite effect. A quiet analysis space with a whiteboard for complex questions is the exception that works.',
      team: 'Collaboration spaces are only worthwhile for the Thinker when they have substance and are well prepared. Not open brainstorm sessions but structured formats with a sharp, substantive question.',
      learning: 'Learning spaces are valuable to the Thinker provided they offer depth. Self-study, access to documentation and knowledge structures that allow deeper exploration are an excellent fit.',
      retreat: 'Retreat spaces are just as essential to the Thinker as focus spaces. After intensive thinking, recovery is necessary. A quiet place to withdraw out of sight of others is not a luxury but a necessity.',
      social: 'Informal spaces are more a source of noise than of energy for the Thinker. Chance contact is welcome when it has substance — informal chat costs more than it gives.',
    },
    bytes: 'Logical systems, good documentation, complete information and tools that support deeper work.',
    behavior: 'Give thinking time, substance and preparation. Here quality comes before speed.',
    friction: [
      'Open-plan offices are often cognitive noise for you.',
      'Decisions taken without analysis feel unsafe.',
      'Too many interruptions make you less effective.',
    ],
    consequence: [
      'You withdraw when depth has nowhere to go.',
      'Your substantive contribution quietly disappears from view.',
      'Quality drops without it being immediately visible.',
    ],
    leadership: [
      'Provide information on time and in full.',
      'Respect thinking time.',
      'Value thoroughness visibly.',
    ],
    needs: [
      'I need the full picture, and in good time — I cannot judge half a story.',
      'Give me thinking time before you expect an answer.',
      'Being thorough costs me time; I want to know that it is seen.',
    ],
    lquote: 'Rush this person and you often lose precisely the thing that safeguards the quality.',
    quote: 'Rush you and you lose exactly the thing you safeguard the quality with.',
    mismatch: [
      'Work floors with constant noise.',
      'Cultures where speed matters more than substance.',
      'Unprepared meetings without a sharp question.',
    ],
    energycost: [
      'Interruptions.',
      'Decisions without analysis.',
      'Too little calm to go deeper.',
    ],
    wd: [
      'Quiet focus spaces and acoustic calm',
      'Clear provision of information',
      'Places where concentration is protected',
    ],
    ls: [
      'Allow time to think',
      'Ensure good preparation',
      'Protect quality in the process',
    ],
    ct: [
      'Reinforces the Maker well',
      'Can rub against the Achiever on pace',
      'Brings quality and depth to teams',
    ],
  },
  verbinder: {
    name: 'Connector',
    kw: ['people', 'contact', 'cohesion'],
    short: 'You connect. For you, good work only really happens when people feel seen and connected.',
    motivation: 'Human contact, collaboration and an atmosphere in which people genuinely find each other.',
    energy_from: 'Encounter, collaboration, alignment and meaningful contact.',
    frustration: 'Isolation, distance, a soured atmosphere and working without real connection.',
    strengths: 'Brings people together, senses the undercurrent and keeps collaboration human.',
    bricks: 'Meeting points, coffee corners, open collaboration zones and routing that encourages chance encounters.',
    bricksProfileText: {
      focus: 'Focus spaces are functional for the Connector but not a source of energy. Working alone in silence for long stretches costs more than it gives. Short focus moments are fine — not a pattern that fills the day.',
      work: 'Standard workstations are fine for the Connector when they sit close to the team and allow spontaneous contact. Isolated workstations far from the walking routes have the opposite effect.',
      meeting: 'Meeting spaces are essential for the Connector — but spaces that make real contact possible. Not cold boardrooms but environments that invite conversation, eye contact and genuine connection.',
      project: 'Creative spaces interest the Connector when they make collaboration easier. Making or devising something together is energising — solo creative work less so.',
      team: 'Collaboration spaces are the Connector\'s core. Open, accessible zones that invite spontaneous collaboration and leave room for both relationship and task are the ideal working climate.',
      learning: 'Learning spaces are most valuable to the Connector when they are social. Learning together, sharing knowledge and hearing from each other are an excellent fit — self-study in isolation much less so.',
      retreat: 'Retreat spaces are rarely a deliberate choice for the Connector. When the Connector withdraws, it is a signal that something is wrong in the collaboration or the atmosphere — not a normal pattern.',
      social: 'Informal spaces are the Connector\'s core. Warm meeting zones, coffee corners and routing that invites chance contact are not a side issue but the heart of the working environment for this archetype.',
    },
    bytes: 'Communication tools that feel human and make real contact easy.',
    behavior: 'Make room for contact, relationship and alignment. Connection is not a side issue but a precondition.',
    friction: [
      'Too much digital distance costs you energy.',
      'A cold, task-driven atmosphere makes you less effective.',
      'When encounter has nowhere to happen, collaboration dries up.',
    ],
    consequence: [
      'You are often the first to see where things are rubbing.',
      'Without connection the team grows quieter and more distant.',
      'Important signals get missed when you are given no room.',
    ],
    leadership: [
      'Deliberately make time for contact.',
      'Involve this persona early in change.',
      'Make sure encounter is possible and normal.',
    ],
    needs: [
      'I need real contact, not just a meeting in the calendar.',
      'Involve me early in change, not once it has been decided.',
      'I want meeting each other to be normal in our work, not an extra.',
    ],
    lquote: 'A team without a connector only notices what is missing once everyone still works together but nobody feels connected.',
    quote: 'A team without you only notices what has gone once everyone still works together but nobody feels connected.',
    mismatch: [
      'Workplaces designed for focus alone.',
      'Teams whose only contact is digital.',
      'Cultures where relationship is secondary to task.',
    ],
    energycost: [
      'Working alone without contact.',
      'No room for conversation.',
      'Too much distance in collaboration.',
    ],
    wd: [
      'Warm meeting zones',
      'Informal meeting spaces',
      'Routing that supports spontaneous encounter',
    ],
    ls: [
      'Ask how things really are',
      'Make encounter part of the work',
      'Use this persona as an early warning',
    ],
    ct: [
      'Strong alongside the Team Player',
      'Can rub against the Achiever',
      'Guards the human side of collaboration',
    ],
  },
  teamspeler: {
    name: 'Team Player',
    kw: ['loyalty', 'trust', 'winning together'],
    short: 'You work for the team. What counts for you is not only what you achieve, but above all what you build together.',
    motivation: 'Contributing to a settled group with trust, continuity and shared success.',
    energy_from: 'Working with familiar people, clear roles and a strong sense of team.',
    frustration: 'Shifting teams, unclear relationships and a lack of loyalty.',
    strengths: 'Brings calm, loyalty and collaboration. Holds teams together.',
    bricks: 'Fixed team spaces, recognisable zones and environments in which a team can genuinely organise itself as a team.',
    bricksProfileText: {
      focus: 'Focus spaces are fine for the Team Player provided they are close to the team. Working in a fully isolated focus room far from the team feels uncomfortable. A quiet spot within the team area works better.',
      work: 'Standard workstations are ideal for the Team Player when they are fixed and recognisable. A settled place within the team, always available — this gives an anchor and strengthens the sense of team. No hot-desking.',
      meeting: 'Meeting spaces are essential for the Team Player — but fixed, recognisable spaces that belong to the team. A settled team meeting space that supports team rituals and continuity works better than rotating boardrooms.',
      project: 'Creative spaces interest the Team Player when the team uses them together. Solo creative work in a creative zone is a weaker fit for this archetype.',
      team: 'Collaboration spaces are the Team Player\'s core. Fixed team zones with a recognisable layout and rituals work better than rotating open collaboration rooms. Continuity and familiarity are the foundation.',
      learning: 'Learning spaces are relevant to the Team Player when they are team-oriented. Learning together and sharing knowledge within the team suits better than individual learning tracks.',
      retreat: 'Retreat spaces are rarely a deliberate choice for the Team Player. When the Team Player withdraws, it is a sign of discomfort with the team dynamic — not a normal working pattern.',
      social: 'Informal spaces are valuable to the Team Player when they are team-oriented. A shared lunch, a regular coffee spot for the team and rituals anchored in the space bring energy and strengthen the sense of team.',
    },
    bytes: 'Shared systems, team overviews, settled processes and tools that make collaboration predictable.',
    behavior: 'Work with rhythm, clarity and shared agreements. This persona thrives on continuity.',
    friction: [
      'Shifting teams feel like structural unrest.',
      'Having no home base makes you less solid.',
      'Too little attention to the sense of team makes your strength invisible.',
    ],
    consequence: [
      'Your loyalty fades when the team foundation is missing.',
      'Unrest translates into lower engagement.',
      'Cohesion disappears when continuity is absent.',
    ],
    leadership: [
      'Be predictable and consistent.',
      'Announce changes in good time.',
      'Make team dynamics discussable.',
    ],
    needs: [
      'What I need from you most is predictability: do what you say, say what you do.',
      'Tell me in good time what is changing, even if it is not settled yet.',
      'I want us to be able to talk about the team when things chafe there.',
    ],
    lquote: 'Treat the team as small change and this person loses their foundation.',
    quote: 'Treat the team as small change and you lose your foundation.',
    mismatch: [
      'Hot-desking and constantly shifting groups.',
      'A culture where everyone mainly works for themselves.',
      'Too much reorganisation without a cushion.',
    ],
    energycost: [
      'Shifting teams.',
      'No settled base.',
      'Uncertainty in collaboration.',
    ],
    wd: [
      'Fixed team spaces',
      'Recognisable zones for teams',
      'Rituals and routines anchored in the space',
    ],
    ls: [
      'Protect team continuity',
      'Provide clarity in roles',
      'Involve the team in changes',
    ],
    ct: [
      'Strong alongside the Connector',
      'Can be unsettled by the Innovator',
      'Brings stability to collaboration',
    ],
  },
  zekerzoeker: {
    name: 'Stabiliser',
    kw: ['structure', 'calm', 'clarity'],
    short: 'You make sure things add up. Clarity, structure and predictability are what make you strong.',
    motivation: 'Overview, stability and knowing where you stand.',
    energy_from: 'Clear expectations, structure, calm and a predictable environment.',
    frustration: 'Chaos, last-minute changes and ambiguity.',
    strengths: 'Brings calm, reliability and process quality. Spots quickly where something is off.',
    bricks: 'Fixed workstations, clear zones, legible routing and as little unnecessary change as possible.',
    bricksProfileText: {
      focus: 'Focus spaces are essential for the Stabiliser — preferably fixed, recognisable places that are available every day. No hunting around, no uncertainty about availability. Predictability is the key.',
      work: 'Standard workstations are the Stabiliser\'s most important workplace. A fixed spot that is always available, ergonomically well set up and close to familiar colleagues. No hot-desking — certainty about your own place is a basic condition.',
      meeting: 'Meeting spaces only give the Stabiliser energy when the discussion is well prepared and structured. Impromptu meetings or discussions without an agenda cost a disproportionate amount of energy.',
      project: 'Creative spaces are a poor fit for the Stabiliser. Messiness, experiment and uncertainty about what is expected are the opposite of what gives energy.',
      team: 'Collaboration spaces are fine for the Stabiliser when the ground rules are clear. Who sits where, what the goal is, how long it takes — once that is clear, collaborating works well. Without structure it costs too much energy.',
      learning: 'Learning spaces interest the Stabiliser when they offer structure. Organised knowledge sessions and step-by-step learning tracks suit better than open, experimental learning environments.',
      retreat: 'Retreat spaces are an essential buffer for the Stabiliser during uncertainty or high workload. A fixed place to withdraw that is always available gives an anchor and a sense of security.',
      social: 'Informal spaces are pleasant for the Stabiliser in a small, familiar circle. Large open meeting zones full of unfamiliar faces create tension rather than energy.',
    },
    bytes: 'Clear systems, stable processes and reliable information.',
    behavior: 'Communicate early, clearly and consistently. This persona needs predictability in order to function well.',
    friction: [
      'Ambiguity is not a minor irritation for you but a genuine blocker.',
      'Last-minute changes cost you a disproportionate amount of energy.',
      'Too much change at once takes away your stability.',
    ],
    consequence: [
      'Chronic ambiguity leads to tension.',
      'You deliver less well when you constantly have to switch without an anchor.',
      'Resistance grows as clarity disappears.',
    ],
    leadership: [
      'Communicate early and in full.',
      'Make expectations explicit.',
      'Offer structure in uncertain periods.',
    ],
    needs: [
      'Tell me early what is going on — uncertainty costs me more than bad news.',
      'Make your expectations explicit; otherwise I will fill them in myself.',
      'When things get uncertain I need structure, not extra freedom.',
    ],
    lquote: 'For this person ambiguity is not a challenge but an erosion of their ability to work.',
    quote: 'For you, ambiguity is not a challenge but something that erodes your ability to work.',
    mismatch: [
      'Environments with constant change.',
      'Vague expectations or incomplete information.',
      'Workplaces without fixed anchors.',
    ],
    energycost: [
      'Unpredictability.',
      'Too much change.',
      'Unclear boundaries.',
    ],
    wd: [
      'Fixed anchor spaces',
      'A calm, legible layout',
      'Clear structures and predictable zones',
    ],
    ls: [
      'Give clarity early',
      'Treat structure as quality',
      'Respect the need for predictability',
    ],
    ct: [
      'A strong base alongside the Achiever',
      'Often clashes with the Maker',
      'Brings calm and reliability',
    ],
  },
  vernieuwer: {
    name: 'Innovator',
    kw: ['thinking ahead', 'opportunities', 'change'],
    short: 'You see what could be different. For you change is not mainly a risk, but mainly potential.',
    motivation: 'Spotting opportunities, creating momentum and building towards something better.',
    energy_from: 'New ideas, momentum, change and an eye on the future.',
    frustration: 'Standing still, clinging to the past and organisations without a vision.',
    strengths: 'Brings direction, renewal and future-focused thinking.',
    bricks: 'Inspiring workstations, innovation zones, project rooms and environments that make momentum and renewal visible.',
    bricksProfileText: {
      focus: 'Focus spaces are rarely where the Innovator\'s best ideas appear. Too much silence and structure dampen forward-looking thinking. Short focus moments are functional for working things out — not the engine.',
      work: 'Standard workstations are fine for the Innovator when they are flexible and inspiring. A dull fixed desk in a routine setting slows things down. Places that normalise thinking about the future work better.',
      meeting: 'Meeting spaces are valuable to the Innovator when the discussion is strategic and future-focused. Operational catch-ups cost energy — vision conversations and strategy sessions give it.',
      project: 'Creative spaces are the Innovator\'s core. Innovation zones, experimentation rooms and environments that normalise momentum and renewal are the ideal habitat. The more room for experiment and future-focused thinking, the better.',
      team: 'Collaboration spaces are essential to the Innovator when they are aimed at strategy and exploration. Large open innovation zones where ideas can collide and new directions become visible are the ideal collaborative climate.',
      learning: 'Learning spaces interest the Innovator when they give access to new knowledge, trends and future-focused thinking. Not to polish existing skills but to explore new directions.',
      retreat: 'Retreat spaces are a functional reset for the Innovator — nothing more than that. A short break in order to look ahead again with fresh energy. Not a structural part of the working pattern.',
      social: 'Informal spaces interest the Innovator when they enable cross-pollination. Contact with people outside their own team or organisation gives the most energy and the most new ideas.',
    },
    bytes: 'Tools for innovation, experimentation, trends and fast iteration.',
    behavior: 'Make room for ideas, strategy and exploration. Involve this persona in change and direction.',
    friction: [
      'Steering on the short term alone pinches for you immediately.',
      'Bureaucracy feels like an energy leak to you.',
      'If ideas never land, you stop sharing them.',
    ],
    consequence: [
      'You grow quieter when renewal gets no platform.',
      'The organisation loses sight of future opportunities.',
      'Your energy shifts to places where there is movement.',
    ],
    leadership: [
      'Give strategic room.',
      'Connect vision to concrete experiments.',
      'See this persona as a co-designer.',
    ],
    needs: [
      'I need room to look further ahead than this quarter.',
      'Let me try an idea out small instead of only discussing it.',
      'Involve me in where we are going, not only in how we get there.',
    ],
    lquote: 'Without a platform this person builds their own — and that is rarely inside the same organisation.',
    quote: 'Give you no platform and you will build your own — and that is rarely inside the same organisation.',
    mismatch: [
      'Environments with no room for renewal.',
      'Decision-making that keeps slowing renewal down.',
      'Cultures without a view of the future.',
    ],
    energycost: [
      'No room for ideas.',
      'Slow decision-making.',
      'Being stuck in old patterns.',
    ],
    wd: [
      'Inspiring innovation zones',
      'Places where future-focused work is visible',
      'Room for experiment and momentum',
    ],
    ls: [
      'Give vision and a mandate',
      'Connect ideas to action',
      'Use this persona for strategy and transition',
    ],
    ct: [
      'Strong alongside the Achiever',
      'Can destabilise Stabilisers',
      'The engine of change in teams',
    ],
  },
};

export default archetypes;
